/*
 * @CopyRight    : 广州仰望星空云科技有限公司
 * @Author       : Kyle
 * @Email        : linyukai@xinyu668.com
 * @CreateTime   : 2023-08-18 14:01:43
 * @Description  : 客户端交互兼容方法
 */

import { browser } from '@ywxk/util/browser'
import { base64ToUint8Array } from '@ywxk/util/format'
import { saveInLocal } from '@ywxk/util/handler'
import { validate } from '@ywxk/util/validate'
import { decode } from '@ywxk/request'
import { constConf } from '@GLOBAL/constConf'
import { client } from '@UTILS/client'
import { appData, bridgeClient } from '@GLOBAL/bridge'
import { getEnterConf } from '@UTILS/enter'
import { toast } from './toast'
import { versionCompare } from '@ywxk/util/versionCompare'

const { PC_BROWSER_ID, IS_DEBUG, SECHEME, IS_LOCAL, PC_URL } = constConf
const { isAndroid, isIOS } = browser
export { bridgeClient }

interface CustomWindow extends Window {
  pcBridgeClient: {
    [propName: string]: (T?) => void
  }
  bridgeClient: {
    [propName: string]: (T?) => void
  }
  [propName: string]: any
}

declare const window: CustomWindow

/**
 * 跳转页面
 * @param {} type 入口配置对应的key || 完整地址
 */
export async function jumpUrl(type, search = '') {
  let url = type || ''
  const isUrl = validate(['url', 'isScheme'], type)
  if (isUrl) {
    if (search) {
      const sign = type.indexOf('?') !== -1 ? '&' : '?'
      url = type + sign + search
    } else {
      url = type
    }
  } else {
    // 已废弃这段逻辑,直接用goCharge跳充值
    // 充值是单独路由,因为涉及到直接跳转,所以耦合进来
    // if (type === 'officialAccounts' && !isAndroid) return goCharge()
    url = (await getEnterConf(type, search)) || {}
    url = (url as any)?.wholeUrl
    if (!url) return console.error(`找不到对应的路由,type-${type},search-${search}`, url)
  }
  // pc
  if (client.isPc && validate('isScheme', url || type)) return bridgeClient.toProcessRout(url || type)
  // 移动端
  !IS_LOCAL && client.isApp && appData ? bridgeClient.jumpInternalRoute({ internalUrl: url }) : (location.href = url)
}

/**
 * 跳转充值
 * @param type 跳转类型 0-直接打开充值页面 1-进入房间并且打开充值链接 2-关闭当前弹窗并且跳转路由链接
 * @param url 跳转链接
 */
export const goCharge = async (type = 0, url = '') => {
  // 解决部分机型不传默认值时浏览器会传事件对象问题
  type = typeof type === 'object' ? 0 : type
  if (client.isPc) return bridgeClient.createNewWebPage(`${PC_URL}#/wallet/NewRecharge`, { width: 640, height: 530 })
  if (isAndroid || !client.isApp) {
    let url = (await getEnterConf('officialAccounts', 'v=20240827')) || {}
    url = (url as any)?.wholeUrl
    return location.href = url
  }
  if (isIOS) {
    url = url || `${SECHEME}?dyaction=charge`
    switch (type) {
      case 0:
        bridgeClient.showRecharge()
        break
      case 1:
        url = window.btoa(url)
        url = window.encodeURIComponent(url)
        jumpUrl(`${SECHEME}?dyaction=room&roomid=0&entertype=0&sourceid=0&Ptype=2&routerurl=${url}`)
        break
      case 2:
        bridgeClient.finishWebAndJumpRoute({ routerUrl: url, halfwebview: false })
        break
    }
  }
}

interface SubscribeItem {
  // cmdid: 解析协议
  [propName: string]: string
}
/**
 * 订阅方法
 * @param SubscribeItem
 * @param callback 触发回调
 */
export const addSubscribe = (data: SubscribeItem, callback: (data, msg) => void) => {
  if (!client.isClient) return Promise.reject('非客户端环境')
  // 收集订阅ids
  const ids = Object.keys(data).map((item) => +item)

  window.subscribeCallback = function (msg) {
    // 特殊处理一下格式
    if (!msg.activityCmdId && typeof msg == 'string') msg = JSON.parse(msg)

    // pc可能多个页面订阅推送，要过滤
    if (!ids.includes(msg.activityCmdId)) return

    // 有些不需要解析
    let res = null

    // 特殊处理一下格式
    if (data[msg.activityCmdId] && msg?.bin) res = decode(data[msg.activityCmdId], client.isFlutter || isIOS ? base64ToUint8Array(msg.bin.replace(/[\r\n↵]/g, '')) : new Uint8Array(msg.bin))

    IS_DEBUG && console.info('推送id: ' + msg.activityCmdId, res, msg)
    // 触发回调,返回解析后数据和原始数据
    callback && callback(res, msg)
  }

  // pc没加监听方法,只需要加回调全部推送,我们自己区分,ids用于批量监听，id为了兼容旧版本只能监听一个,pcBrowserId为了给pc做区分窗口
  if (bridgeClient.subscribe) {
    // bridgeClient.subscribe(JSON.stringify({ ids, id: ids[0], pcBrowserId: PC_BROWSER_ID }))
    bridgeClient.subscribe({ ids, id: ids[0], pcBrowserId: PC_BROWSER_ID })
    return Promise.resolve()
  } else {
    return Promise.reject('不存在subscribe方法')
  }
}

/**
 * @description: APP跳房间
 * @param      {*} roomid
 */
export const jumpRoom = (roomid) => {
  if (client.isPc) return bridgeClient?.enterUserRoom(roomid)

  const secheme = `${SECHEME}?dyaction=room&roomid=${roomid}&entertype=2&sourceid=0&opentype=0`
  if (isAndroid) return jumpUrl(secheme)
  bridgeClient.finishWebAndJumpRoute({ routerUrl: secheme })
}

/**
 * @description: APP打开个人名片
 * @param      {*} pid
 * @param      {*} orderId 订单id，用于一起玩
 * @param      {*} type 来源，用于一起玩: 5-新一起玩
 */
export const openPersonalCard = (pid, orderId = '', type = 0) => {
  if (!pid) return
  if (client.isPc) return orderId ? bridgeClient.showVisitingCardWithJson({ uid: pid, playTogetherOrderId: orderId, reportSourceType: type }) : bridgeClient.showVisitingCard(pid)
  bridgeClient?.getPlayerData?.({ pid, orderId, type })
}

// 虚拟马甲弹出名片
export const showVestCard = (pid) => {
  if (!pid) return
  if (client.isPc) {
    const targetVer = '12.1.1'
    const version = appData?.version?.split('-')[0];
    if (versionCompare(version, targetVer) < 0) return;
    return bridgeClient.showVestCard({ id: pid })
  }
  bridgeClient?.getPlayerData?.({ pid })
}

/**
 * @description: 保存图片到本地
 * @param      {SavePic} opt
 */
type SavePic = { fileUrl: string; fileName?: string; callback: (result, info) => void }
export const savePic = (opt: SavePic) => {
  if (!opt.fileUrl) return
  if (client.isPc) return saveInLocal(opt.fileUrl, opt.fileName)
  if (client.isYes) {
    window.jsBridgeClient.savePicture({ url: opt.fileUrl }, ({ code }) => {
      opt.callback(Number(code) === 1, '保存相册失败')
    })
  } else {
    window.webSaveFileCallback = ({ result, info }) => opt.callback(result, info)
    bridgeClient.webSaveFile({ fileTye: 1, fileUrl: opt.fileUrl, fileName: opt.fileName })
  }
}

/**
 * @description: 关闭页面
 * @param      {*} halfwebview iOS特有,是否半屏
 */
export const close = (halfwebview?: boolean) => {
  if (!client.isClient) return
  if (client.isPc) return bridgeClient?.finishWebView()
  if (isIOS) return bridgeClient?.finishWebView({ halfwebview })
  bridgeClient?.finishWebView()
}


/**
 * @description: 唤起app打开页面
 * @param      {string} url 唤起地址
 * @param      {*} title 标题
 * @param      {*} secheme 替代默认
 * @return     {*}
 */
export const tryCallApp = (url: string, title = '', secheme = '') => {
  let targetUrl = `${secheme || SECHEME}?dyaction=web&url=${encodeURIComponent(url)}`
  if (title) targetUrl += `&title=${encodeURIComponent(title)}`
  // 站外带scheme跳转
  jumpUrl(targetUrl)
}

/**
 * 获取好友列表
 * @param params
 * isSearch -- 是否为搜索
 * type -- 获取好友的类型 0-占位不用 1-所有分类 2-好友 3-关注 4-粉丝 5-屏蔽
 * keyword -- 搜索好友名字关键字
 * @returns number[]
 *
 */
export const getFriendList = async (data: { isSearch: boolean; type: number; searchKey: string }) => {
  const { isSearch = false, type = 2, searchKey = '' } = { ...(data || {}) }
  return new Promise((resolve, reject) => {
    const api = isSearch ? bridgeClient?.searchRelationship : bridgeClient?.getRelationshipList
    const params = isSearch
      ? {
        type, // 0-占位不用 1-所有分类 2-好友 3-关注 4-粉丝 5-屏蔽
        keyword: searchKey.trim(),
      }
      : { type }
    try {
      api(params, (res) => {
        const ids = []
        const list = Object.values(res)
        list.forEach((ary) => {
          ids.push(...ary)
        })

        resolve(ids)
      })
    } catch (err) {
      reject(err)
    }
  })
}

/*
 * 打开外部浏览器(safari等)
 * @params { String } url, 需要打开的链接
 */
export function openBrowser(url, needFinish = false) {
  if (client.isPc) {
    bridgeClient.openIExplorer(url)
  } else {
    client.isFlutter ? bridgeClient.openBrowser({ url }) : bridgeClient.goToOutSide({ url, needFinish })
  }
}

// 打开新的h5页面不关闭原来的 needAnalysis: 是否需要区分路由url
export function openNewPage(title, url, width = 477, height = 900, needAnalysis) {
  if (needAnalysis) {
    const routePrefix = ['mizhua://m.mizhua.com', 'yes://www.bantangyuyin.com', 'caramel://m.mizhua.com', 'neon://m.yangqikg.com'];
    if (routePrefix.find(item => url.indexOf(item) !== -1)) { // 传入的url是路由类型，pc端要做特殊处理
      const urlSplit = url.split('url=');
      let decodeUrl = decodeURIComponent(urlSplit[1]);
      const reg = new RegExp('(^|&)title=([^&]*)(&|$)', 'i');
      // 默认取路由中的title
      let t = decodeUrl.match(reg)?.[2] || title;
      title = t;
      url = decodeUrl;
    }

  }

  if (client.isApp) {
    bridgeClient?.openPage({
      title,
      url,
    })
  } else {
    // pc那边限制了最小宽度650，高度530,导致这俩参数有点问题，
    bridgeClient?.createNewWebPage(url, { width, height })
  }
}

/**
 * @description: 隐藏导航栏。可用于网页全屏展示(仅支持app,优先使用路由参数方式控制)
 * @param      {*} ishidden 是否隐藏
 * @param      {*} statusBarTextTransparent
 * @param      {*} statusBarBgColorStr 导航栏背景色
 * @return     {*}
 */
export function webNavigitionHidden(ishidden = true, statusBarTextTransparent = true, statusBarBgColorStr = '#000000') {
  if (client.isApp) {
    bridgeClient?.webNavigitionHidden({
      ishidden,
      statusBarTextTransparent,
    })
    bridgeClient?.webStatusBarBgColorStr({
      statusBarBgColorStr,
    })
  }
}

/**
 * @description: 获取刘海屏高度(仅支持app)
 */
export function webStatusBarHeight(): Promise<Number> {
  return new Promise((resolve, reject) => {
    bridgeClient?.webStatusBarHeight((res) => {
      let statusBarHeight = Number(typeof res === 'object' ? res.statusbarheight : res)
      const barHeight = client.isApp ? (statusBarHeight + 20) * 0.1333 : 0
      resolve(barHeight)
    })
  })
}

// 客户端上传图片
export function uploadImage(callback) {
  if (!window.uploadReportImageCallback) {
    window.uploadReportImageCallback = (data) => {
      if (Object.prototype.toString.call(data) === '[object String]') {
        try {
          data = JSON.parse(data)
        } catch (e) {
          toast('上传失败')
        }
      }

      if (data.data) {
        callback(data.data)
        // 事件回调
        toast('上传成功')
      } else {
        toast(data.msg || '上传失败')
      }
    }
  }

  if (bridgeClient.uploadReportImage) {
    bridgeClient.uploadReportImage()
    return Promise.resolve()
  } else {
    return Promise.reject('不存在uploadReportImage方法')
  }
}

/**
 * 开始录音
 * @params { minDuration, maxDuration },
 * @returns { Prmoise }
 */
export function startRecording({ minDuration, maxDuration }) {
  return new Promise((resolve, reject) => {
    if (client.isApp) {
      bridgeClient.startRecording({ minDuration, maxDuration }, () => { })
      resolve()
    } else {
      bridgeClient
        .startRecording(maxDuration)
        .then((data) => resolve(data))
        .catch((err) => reject(err))
    }
  })
}

//唤醒QQ
function handleWakeQQ(qqNumber = '2853992296') {
  //兼容ios浏览器打开
  if (browser.isIOS) {
    // console.log('要唤起的scheme', `mqqwpa://im/chat?chat_type=wpa&uin=${qqNumber}&version=1&src_type=web&web_src=oicqzone.com`)
    location.href = `mqqwpa://im/chat?chat_type=wpa&uin=${qqNumber}&version=1&src_type=web&web_src=oicqzone.com`
    return
  }
  location.href = `http://wpa.qq.com/msgrd?v=3&uin=${qqNumber}&site=qq&menu=yes`
}

// 调起客户端的普通客服
export function handleWakeService(data) {
  const { mark, isQQ, isQidian, isHeart8 } = data || {}
  const { QQ, ORIGIN_ENV_PROJECT, ENV, SECHEME } = constConf

  if (isQQ && QQ) {
    // 跳转qq客服
    handleWakeQQ(QQ)
  } else if (isQidian) {
    // 跳转企点客服
    const url =
      'https://static.qidian.qq.com/2/chat/h5/index.html?linkType=1&env=ol&kfuin=2885717673&fid=159&key=f5509f24881025d900e87da7be6dc753&cate=7&type=10&ftype=1&_type=wpa&qidian=true&subSource=1'
    jumpUrl(url)
  } else if (!client?.isClient) {
    // 站外带scheme跳转
    let toUrl = `${ORIGIN_ENV_PROJECT}/commonality/customerService/index.html?source=app&noTurn=1`
    if (ENV !== 'prod') {
      toUrl += '&debug=1'
    }
    if (isHeart8) {
      toUrl += '&subSource=1'
    }
    jumpUrl(`${SECHEME}?dyaction=web&url=${encodeURIComponent(toUrl)}`)
  } else {
    // 跳转app客服
    jumpUrl('customerService', `source=app&noTurn=1` + (mark ? `&mark=${mark}` : '') + (isHeart8 ? `&subSource=1` : ''))
  }
}

// 获取app域名信息
export async function getAppLinks() {
  if (client.isPc) {
    return await bridgeClient.getAppLinks()
  }
  return new Promise((resolve, reject) => {
    bridgeClient.getAppLinks((res) => {
      resolve(res)
    })
  })
}

export async function changeHost(type, link, cost) {
  const params = {
    type,
    link,
    cost,
  }
  if (client.isPc) {
    return await bridgeClient.changeHost(params)
  }
  return new Promise((resolve, reject) => {
    bridgeClient.changeHost(params, (res) => {
      resolve(res)
    })
  })
}

// 获取麦位信息
export function getVoiceSeat(callback) {
  if (!client.isClient) return Promise.reject('非客户端环境')

  window.voiceSeatCallback = (msg) => {
    if (typeof msg == 'string') {
      try {
        msg = JSON.parse(msg)
      } catch (err) {
        // 安卓json格式不对，
        return toast('请更新app至最新版本体验此功能~')
      }
    }
    callback && callback(msg)
  }

  if (bridgeClient?.getVoiceSeat) {
    bridgeClient.getVoiceSeat()
  }
}

// 赠送弹窗礼物，调起原生弹窗
export function sendScreenGift(params, callback) {
  if (!client.isClient) return Promise.reject('非客户端环境');
  window.sendScreenGiftCallBack = (msg) => {
    if(typeof msg == 'string') msg = JSON.parse(msg);
    callback && callback(msg);
  }
  console.log(bridgeClient);
  if(bridgeClient?.sendScreenGift) {
    bridgeClient.sendScreenGift(params);
  }
}

/**
 * 前往身份卡编辑页面
 * @param uid 自己的id，默认可不传
 * @param typeid 身份卡类型ID，1-唱歌 2-互动
 */
export function goRoleCard(data) {
  const { uid = 0, typeid = 1 } = data || {}
  jumpUrl(`${SECHEME}?dyaction=to_edit_role_card&uid=${uid}&typeid=${typeid}`)
}
